import { formatDate } from '@angular/common'
import { Injectable } from '@angular/core'
import type * as ExcelJS from 'exceljs'
import { firstValueFrom } from 'rxjs'
import { Attendance, AttendanceReportItem, ContactReportItem, ExcelReportSummaryData, ImportationStatusTranslateEnum, LeadSourceItem, LeadSourceKpisResult, Message, MessageTypeEnum, ReportService, UtilHelper } from '../../public-api'
import { DateUtil } from '../helpers/date.util'
import { Importation } from '../models/importation.model'
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces'

export type ReportColumn = {
  header: string;
  key: string;
  width?: number;
};

@Injectable()
export class ExportService {
  constructor(private reportService: ReportService) {
  }

  /**
   * ExcelJS and pdfMake are loaded on demand. Importing them statically puts ~3.3 MB
   * (exceljs + pdfmake + its embedded fonts) in the initial bundle, even though export
   * is a rare, user-triggered action.
   */
  private excelJs: typeof ExcelJS | null = null;
  private pdfMakePromise: Promise<any> | null = null;

  private async getExcelJS(): Promise<typeof ExcelJS> {
    if (!this.excelJs) {
      const module: any = await import('exceljs');
      this.excelJs = (module?.Workbook ? module : module?.default) as typeof ExcelJS;
    }
    return this.excelJs;
  }

  private getPdfMake(): Promise<any> {
    // The promise itself is memoised so concurrent exports share a single load.
    this.pdfMakePromise = this.pdfMakePromise ?? this.loadPdfMake();
    return this.pdfMakePromise;
  }

  private async loadPdfMake(): Promise<any> {
    const pdfMakeModule: any = await import('pdfmake/build/pdfmake');
    const pdfMake: any = pdfMakeModule?.default ?? pdfMakeModule;

    // `vfs_fonts` must be evaluated AFTER pdfmake: it registers itself against the global
    // pdfMake when that already exists. Loading both in parallel makes that side effect racy,
    // which leaves the fonts unregistered ("File 'Roboto-Medium.ttf' not found in virtual file system").
    const pdfFontsModule: any = await import('pdfmake/build/vfs_fonts');
    const pdfFonts: any = pdfFontsModule?.default ?? pdfFontsModule;

    // pdfmake >= 0.2.20 exports the raw `{ 'Roboto-Regular.ttf': ... }` map;
    // older builds nested it under `.vfs` / `.pdfMake.vfs`.
    const vfs: any = pdfFonts?.vfs ?? pdfFonts?.pdfMake?.vfs ?? pdfFonts;

    // Register explicitly instead of relying on the side effect above.
    if (typeof pdfMake.addVirtualFileSystem === 'function') {
      pdfMake.addVirtualFileSystem(vfs);
    } else {
      pdfMake.vfs = { ...(pdfMake.vfs ?? {}), ...vfs };
    }

    return pdfMake;
  }

  private reportAttendaceFields = [
    { header: 'Nome', key: 'name', width: 30 },
    { header: 'Telefone', key: 'phone', width: 20 },
    { header: 'Canal', key: 'channel', width: 20 },
    { header: 'Departamento', key: 'department', width: 25 },
    { header: 'Atendente', key: 'user', width: 25 },
    { header: 'Status', key: 'status', width: 20 },
    { header: 'Tags', key: 'tags', width: 30 },
    { header: 'Avaliação', key: 'feedbackCSAT', width: 20 },
    { header: 'Duração', key: 'duration', width: 15 },
    { header: 'Início', key: 'createdAt', width: 25 },
    { header: 'Encerrado', key: 'closedAt', width: 35 },
  ];

  private reportAttendaceTabsFields = [
    { header: 'Início', key: 'createdAt', width: 25 },
    { header: 'Nome', key: 'name', width: 30 },
    { header: 'Atendente', key: 'user', width: 25 },
    { header: 'Canal', key: 'channel', width: 20 },
    { header: 'Departamento', key: 'department', width: 25 },
    { header: 'Tabulação', key: 'tabulations', width: 25 },
    { header: 'Observação', key: 'observations', width: 25 },
    { header: 'Duração', key: 'duration', width: 15 },
  ];

  async generateExcel(data: any[]): Promise<any> {
    const ExcelJS = await this.getExcelJS()
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('Relatório-Performance')

    worksheet.columns = [
      { header: 'Nome Canal', key: 'channel', width: 30 },
      { header: 'Departamento', key: 'department', width: 30 },
      { header: 'Atendante', key: 'user', width: 40 },
      {
        header: 'Atendimento (período anterior)',
        key: 'totalAttendancePrevious',
        width: 30,
      },
      {
        header: 'Atendimento (período atual)',
        key: 'totalAttendanceCurrent',
        width: 30,
      },
    ]

    data.forEach((item) => {
      worksheet.addRow({
        channel: item.channel,
        department: item.department,
        user: item.user,
        totalAttendancePrevious: item.totalAttendancePrevious,
        totalAttendanceCurrent: item.totalAttendanceCurrent,
      })
    })

    return await workbook.xlsx.writeBuffer()
  }

  async generateExcelNewContact(data: any[]): Promise<any> {
    const ExcelJS = await this.getExcelJS()
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('Relatório-Performance')

    worksheet.columns = [
      { header: 'Nome Canal', key: 'channel', width: 30 },
      { header: 'Departamento', key: 'department', width: 30 },
      { header: 'Atendante', key: 'user', width: 40 },
      {
        header: 'Novos contatos (período anterior)',
        key: 'totalContactPrevious',
        width: 30,
      },
      {
        header: 'Novos contatos (período atual)',
        key: 'totalContactCurrent',
        width: 30,
      },
    ]

    data.forEach((item) => {
      worksheet.addRow({
        channel: item.channel,
        department: item.department,
        user: item.user,
        totalContactPrevious: item.totalContactPrevious,
        totalContactCurrent: item.totalContactCurrent,
      })
    })

    return await workbook.xlsx.writeBuffer()
  }

  async generateExcelImportation(data: Importation[]): Promise<any> {
    const ExcelJS = await this.getExcelJS()
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('Relatório-Importação')

    worksheet.columns = [
      { header: 'Criado em', key: 'createdAt', width: 30 },
      { header: 'Arquivo', key: 'filename', width: 30 },
      { header: 'Situação', key: 'status', width: 30 },
      { header: 'Total de Contatos', key: 'totalSend', width: 30 },
      { header: 'Novos Contatos', key: 'totalInserted', width: 30 },
      { header: 'Contatos Atualizados', key: 'totalUpdated', width: 30 },
      { header: 'Tags Criadas', key: 'totalCreatedTag', width: 30 },
      { header: 'Contatos com erros', key: 'totalError', width: 30 },
    ]

    data.forEach((item) => {
      worksheet.addRow({
        createdAt: DateUtil.toFormart(item.createdAt, 'dd/MM/yyyy HH:mm:ss'),
        filename: item.filename,
        status: ImportationStatusTranslateEnum[item.status],
        totalSend: item.metadata?.totalSend,
        totalInserted: item.metadata?.totalInserted,
        totalUpdated: item.metadata?.totalUpdated,
        totalCreatedTag: item.metadata?.totalCreatedTag,
        totalError: item.metadata?.totalError,
      })
    })

    return workbook.xlsx.writeBuffer()
  }

  async generateExcelCSAT(data: Attendance[]): Promise<any> {
    const ExcelJS = await this.getExcelJS()
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('Relatório-Importação')

    worksheet.columns = [
      { header: 'Data', key: 'updatedAt', width: 30 },
      { header: 'Nome', key: 'name', width: 30 },
      { header: 'Telefone', key: 'phone', width: 30 },
      { header: 'Canal', key: 'channel', width: 30 },
      { header: 'Departamento', key: 'department', width: 30 },
      { header: 'Atendente', key: 'user', width: 30 },
      { header: 'Nota', key: 'feedbackCSAT', width: 30 },
    ]

    data.forEach((item) => {
      worksheet.addRow({
        channel: item.channel?.name,
        department: item.department?.name,
        user: item.user?.name,
        updatedAt: DateUtil.toFormart(item.updatedAt, 'dd/MM/yyyy HH:mm:ss'),
        name: item.contact?.name,
        phone: item.contact?.phone,
        feedbackCSAT: item.feedbackCSAT,
      })
    })

    return workbook.xlsx.writeBuffer()
  }

  async generateExcelAttendanceReport(data: any[], filters?: any, tabs?: boolean): Promise<any> {
    const ExcelJS = await this.getExcelJS();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Relatório-Atendimentos');
    const cols = tabs ? this.reportAttendaceTabsFields : this.reportAttendaceFields;

    worksheet.columns = cols;

    if (filters) {
      worksheet.mergeCells('A1:K1');
      const filterHeader = worksheet.getCell('A1');
      filterHeader.value = 'Filtros Utilizados';
      filterHeader.font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
      filterHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4B5563' },
      };
      filterHeader.alignment = { vertical: 'middle', horizontal: 'center' };

      let filterRowIndex = 2;
      if (filters.channel) {
        worksheet.mergeCells(`A${filterRowIndex}:K${filterRowIndex}`);
        worksheet.getCell(`A${filterRowIndex}`).value = `Canal: ${filters.channel}`;
        worksheet.getCell(`A${filterRowIndex}`).font = { bold: true, size: 12 };
        filterRowIndex++;
      }
      if (filters.department) {
        worksheet.mergeCells(`A${filterRowIndex}:K${filterRowIndex}`);
        worksheet.getCell(`A${filterRowIndex}`).value = `Departamento: ${filters.department}`;
        worksheet.getCell(`A${filterRowIndex}`).font = { bold: true, size: 12 };
        filterRowIndex++;
      }
      if (filters.user) {
        worksheet.mergeCells(`A${filterRowIndex}:K${filterRowIndex}`);
        worksheet.getCell(`A${filterRowIndex}`).value = `Atendente: ${filters.user}`;
        worksheet.getCell(`A${filterRowIndex}`).font = { bold: true, size: 12 };
        filterRowIndex++;
      }
      if (filters.period) {
        worksheet.mergeCells(`A${filterRowIndex}:K${filterRowIndex}`);
        worksheet.getCell(`A${filterRowIndex}`).value = `Período: ${filters.period}`;
        worksheet.getCell(`A${filterRowIndex}`).font = { bold: true, size: 12 };
        filterRowIndex++;
      }
      if (filters.status) {
        worksheet.mergeCells(`A${filterRowIndex}:K${filterRowIndex}`);
        worksheet.getCell(`A${filterRowIndex}`).value = `Status: ${filters.status}`;
        worksheet.getCell(`A${filterRowIndex}`).font = { bold: true, size: 12 };
        filterRowIndex++;
      }

      filterRowIndex++;
      worksheet.mergeCells(`A${filterRowIndex}:K${filterRowIndex}`);
    }

    const headerRowIndex = worksheet.lastRow ? worksheet.lastRow.number + 1 : 1;
    worksheet.addRow(cols.map(col => col.header));

    worksheet.getRow(headerRowIndex).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4B5563' },
      };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });

    data.map((item: AttendanceReportItem) => {
      worksheet.addRow({
        name: item.contact?.name || '-',
        phone: item.contact?.phone || '-',
        channel: item.channel?.name || '-',
        department: item.department?.name || '-',
        user: item.user?.name || '-',
        status: item.status || '-',
        tags: item.tags || '-',
        feedbackCSAT: item.feedbackCSAT,
        duration: item.duration || '-',
        createdAt: item.createdAt || '-',
        closedAt: item.closedAt || '-',
        tabulations: item.tabulations || '-',
        observations: item.observations || '-',
      });
    });

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > headerRowIndex) {
        row.eachCell((cell) => {
          cell.font = { size: 10, color: { argb: 'FF111827' } };
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });

        if (rowNumber % 2 === 0) {
          row.eachCell((cell) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFF9FAFB' },
            };
          });
        }
      }
    });

    return await workbook.xlsx.writeBuffer();
  }

  async generateExcelReport<T>(data: T[], columns: ReportColumn[], filters?: Record<string, string>, title?: string, summaryData?: ExcelReportSummaryData,): Promise<ArrayBuffer> {
    const ExcelJS = await this.getExcelJS();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Relatório');
    worksheet.columns = columns;

    const totalColumns = columns.length;
    let currentRow = 1;

    if (filters && Object.keys(filters).length > 0) {
      currentRow = this.buildExcelFilterSection(worksheet, filters, totalColumns, currentRow);

    }

    if (title) {
      const endColumn = this.getExcelColumnLetter(totalColumns);
      const titleRow = worksheet.insertRow(currentRow, []);
      titleRow.getCell(1).value = title;
      titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
      titleRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
      titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4CAF50' } };
      worksheet.mergeCells(`A${currentRow}:${endColumn}${currentRow}`);
      titleRow.height = 26;
      currentRow++;
    }

    this.addDataRows(worksheet, data, columns);
    const headerRow = worksheet.getRow(currentRow);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4B5563' }, };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > currentRow) {
        row.eachCell(cell => { cell.font = { size: 10, color: { argb: 'FF111827' } }; cell.alignment = { vertical: 'middle', horizontal: 'center' }; });

        if (rowNumber % 2 === 0) {
          row.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
          });
        }
      }
    });

    if (summaryData) {
      this.createSummarySheet(workbook, summaryData);
    }

    return await workbook.xlsx.writeBuffer();
  }

  private buildExcelFilterSection(worksheet: ExcelJS.Worksheet, filters: Record<string, string>, totalColumns: number, startRow: number): number {
    const endColumn = this.getExcelColumnLetter(totalColumns);

    const titleRow = worksheet.insertRow(startRow, []);
    titleRow.getCell(1).value = 'Filtros Utilizados';
    titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
    titleRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
    titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6B7280' } };
    worksheet.mergeCells(`A${titleRow.number}:${endColumn}${titleRow.number}`);

    const filterEntries = Object.entries(filters);
    for (let i = 0; i < filterEntries.length; i++) {
      const [label, value] = filterEntries[i];
      const row = worksheet.insertRow(titleRow.number + i + 1, []);
      row.getCell(1).value = `${label}: ${value}`;
      row.getCell(1).font = { size: 12 };
      worksheet.mergeCells(`A${row.number}:${endColumn}${row.number}`);
    }

    const blankRow = worksheet.insertRow(titleRow.number + filterEntries.length + 1, []);
    worksheet.mergeCells(`A${blankRow.number}:${endColumn}${blankRow.number}`);

    return blankRow.number + 1;
  }

  private addDataRows<T>(worksheet: ExcelJS.Worksheet, data: T[], columns: ReportColumn[]): void {
    for (const item of data) {
      const row = columns.reduce((acc, col) => {
        acc[col.key] = item[col.key] ?? '-';
        return acc;
      }, {} as Record<string, any>);
      worksheet.addRow(row);
    }
  }

  private createSummarySheet(workbook: ExcelJS.Workbook, summaryData: ExcelReportSummaryData): void {
    const summarySheet = workbook.addWorksheet('Resumo');
    let rowNumber = 1;
    const totalColumns = 4;
    const endColumn = this.getExcelColumnLetter(totalColumns);

    if (summaryData.kpis && Object.keys(summaryData.kpis).length > 0) {
      const kpiTitle = summarySheet.getRow(rowNumber++);
      kpiTitle.getCell(1).value = 'KPIs';
      kpiTitle.getCell(1).font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
      kpiTitle.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
      kpiTitle.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6B7280' } };
      summarySheet.mergeCells(`A${kpiTitle.number}:${endColumn}${kpiTitle.number}`);

      for (const [label, value] of Object.entries(summaryData.kpis)) {
        const row = summarySheet.getRow(rowNumber++);
        row.getCell(1).value = label;
        row.getCell(2).value = value;
        row.eachCell(cell => {
          cell.font = { size: 11 };
          cell.alignment = { vertical: 'middle', horizontal: 'left' };
        });
      }
      rowNumber++;
    }

    if (summaryData.salesByStatus?.length) {
      const title = summarySheet.getRow(rowNumber++);
      title.getCell(1).value = 'Total de Vendas por Status';
      title.getCell(1).font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
      title.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6B7280' } };
      title.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
      summarySheet.mergeCells(`A${title.number}:${endColumn}${title.number}`);

      const header = summarySheet.getRow(rowNumber++);
      header.values = ['Data', 'Status', 'Valor'];
      header.eachCell(cell => {
        cell.font = { bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
      });

      for (const item of summaryData.salesByStatus) {
        for (const serie of item.series) {
          const row = summarySheet.getRow(rowNumber++);
          row.getCell(1).value = item.name;
          row.getCell(2).value = serie.name;
          row.getCell(3).value = serie.value;
        }
      }
      rowNumber++;
    }

    if (summaryData.salesBySource?.length) {
      const title = summarySheet.getRow(rowNumber++);
      title.getCell(1).value = 'Vendas por Origem';
      title.getCell(1).font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
      title.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6B7280' } };
      title.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
      summarySheet.mergeCells(`A${title.number}:${endColumn}${title.number}`);

      const header = summarySheet.getRow(rowNumber++);
      header.values = ['Origem', 'Vendas', 'Percentual'];
      header.eachCell(cell => {
        cell.font = { bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
      });

      const total = summaryData.salesBySource.reduce((acc, cur) => acc + cur.value, 0);
      for (const item of summaryData.salesBySource) {
        const percentual = total > 0 ? `${((item.value / total) * 100).toFixed(2)}%` : '0%';
        const row = summarySheet.getRow(rowNumber++);
        row.getCell(1).value = item.name;
        row.getCell(2).value = item.value;
        row.getCell(3).value = percentual;
      }
    }

    summarySheet.columns.forEach(col => col.width = 20);
  }

  private getExcelColumnLetter(colIndex: number): string {
    let letter = '';
    while (colIndex > 0) {
      const remainder = (colIndex - 1) % 26;
      letter = String.fromCharCode(65 + remainder) + letter;
      colIndex = Math.floor((colIndex - 1) / 26);
    }
    return letter;
  }

  async generateExcelContactReport(data: ContactReportItem[]): Promise<any> {
    const ExcelJS = await this.getExcelJS();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Contatos');
    worksheet.columns = [
      { header: 'Nome', key: 'name', width: 30 },
      { header: 'Telefone', key: 'phone', width: 30 },
      { header: 'Email', key: 'email', width: 30 },
      { header: 'Canal', key: 'channel', width: 30 },
      { header: 'Status', key: 'status', width: 30 },
      { header: 'Data de criação', key: 'createdAt', width: 30 },
      { header: 'Tags', key: 'tags', width: 30 },
      { header: 'Endereço', key: 'address', width: 30 },
      { header: 'Origem', key: 'origin', width: 30 },
      { header: 'Total de atendimentos', key: 'totalAttendances', width: 30 },
    ];
    data.forEach((item) => {
      worksheet.addRow({
        name: item.name,
        phone: item.phone,
        email: item.email,
        channel: item.channel?.name,
        status: item.status,
        createdAt: DateUtil.toFormart(item.createdAt, 'dd/MM/yyyy HH:mm:ss'),
        tags: item.tags,
        address: item.address,
        origin: item.origin,
        totalAttendances: item.totalAttendances,
      });
    });
    return await workbook.xlsx.writeBuffer();
  }

  public async exportConversationToPdf(attendance: Attendance, messages: Message[]): Promise<void> {
    const sorted = messages.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    )

    if (!sorted.length) {
      return
    }

    const imageMap: Map<string, string> = new Map<string, string>();
    const messagesWithImages = sorted.filter(m => !!m.content && m.type === MessageTypeEnum.image);
    for (const message of messagesWithImages) {
      if (message.content && (message.type === MessageTypeEnum.image)) {
        try {
          const base64Result: any = await firstValueFrom(this.reportService.downloadFromFirebaseAsBase64(message.content));

          imageMap.set(message._id, base64Result.base64);
        } catch (error) {
          console.error(`Erro ao carregar imagem: ${error}`);
        }
      }
    };

    const stickerMap: Map<string, string> = new Map<string, string>();
    const messagesWithStickers = sorted.filter(m => !!m.content && m.type === MessageTypeEnum.sticker);

    for (const message of messagesWithStickers) {
      try {
        const base64Result: any = await firstValueFrom(this.reportService.downloadFromFirebaseAsBase64(message.content));
        let webpDataUrl = base64Result.base64;

        if (!webpDataUrl.startsWith('data:image/')) {
          webpDataUrl = `data:image/webp;base64,${webpDataUrl}`;
        }

        const pngDataUrl = await this.convertWebpToPng(webpDataUrl);
        stickerMap.set(message._id, pngDataUrl);

      } catch (error) {
        console.error(`Erro ao carregar sticker: ${error}`);
      }
    }


    const definition = this.buildPdfDefinition(attendance, sorted, imageMap, stickerMap)
    await this.downloadPdf(definition, attendance.contact.phone ?? attendance.contact.whatsapp?.bsuid ?? attendance.contact.name ?? 'contato')
  }

  private buildPdfDefinition(attendance: Attendance, messages: Message[], imageMap: Map<string, string>, stickerMap: Map<string, string>): any {
    const clientName = attendance.contact.name
    const attendant =
      attendance.user?.name ??
      attendance.department?.name ??
      attendance.channel?.name ??
      'Desconhecido'

    return {
      pageSize: 'A4',
      pageMargins: [40, 60, 40, 60],
      content: [
        {
          text: `Protocolo de atendimento: ${attendance.protocol}`,
          style: 'header',
        },
        { text: `Atendente: ${attendant}`, style: 'subheader' },
        {
          text: `Data da exportação: ${DateUtil.toFormart(new Date(), 'dd/MM/yyyy HH:mm:ss')}`,
          style: 'subheader',
        },
        '\n',
        {
          columns: [
            {
              text: `Início: ${DateUtil.toFormart(attendance.createdAt, 'dd/MM/yyyy HH:mm:ss')}`,
              width: '50%',
            },
            {
              text: `Última mensagem: ${DateUtil.toFormart(
                messages.at(-1)?.createdAt ?? '', 'dd/MM/yyyy HH:mm:ss'
              )}`,
              width: '50%',
            },
          ],
        },
        '\n\n',
        ...messages.flatMap((msg) =>
          this.formatMessageBlock(msg, attendant, clientName, imageMap, stickerMap)
        ),
        '\n',
        {
          text: `Total de ${messages.length} mensagens.`,
          style: 'footer',
          alignment: 'center',
        },
      ],
      styles: this.getPdfStyles(),
    }
  }

  convertWebpToPng(webpDataUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);
        const pngDataUrl = canvas.toDataURL('image/png');
        resolve(pngDataUrl);
      };
      img.onerror = (err) => reject(err);
      img.src = webpDataUrl;
    });
  }

  private formatMessageBlock(message: Message, attendant: string, client: string, imageMap: Map<string, string>, stickerMap: Map<string, string>) {
    const isClient = message.user === null;
    const isAutomated = message.automated;
    const hasTranscription = message?.transcribed && message?.transcribed === true;

    const sender = isAutomated
      ? 'Mensagem automática'
      : isClient
        ? `Cliente - ${client}`
        : attendant;

    const alignment = isClient ? 'left' : 'right';
    const bubbleColor = isClient ? '#f0f0f0' : '#DCF8C6';
    const margin = alignment === 'right' ? [80, 0, 0, 5] : [0, 0, 80, 5];

    const setupContent = (message: Message) => {
      let result: any = {
        text: message.content,
        style: 'messageText',
        margin: [0, 0, 0, 15],
      };

      if (message.type === MessageTypeEnum.image) {
        result = {
          image: imageMap.get(message._id) || '',
          width: 200,
          alignment: 'center',
        };
      }

      if (message.type === MessageTypeEnum.sticker) {
        result = {
          image: stickerMap.get(message._id) || '',
          alignment: 'center',
          width: 100,
        };
      }

      if (message.type === MessageTypeEnum.audio) {
        result = {
          columns: [
            {
              text: ' ',
              width: 'auto',
              fontSize: 20,
              alignment: 'center',
              margin: [0, 2, 8, 0],
            },
            {
              stack: [
                {
                  text: hasTranscription ? 'Transcrição da Mensagem de áudio' : 'Mensagem de áudio',
                  style: 'audioTitle',
                  margin: [0, 0, 0, 4],
                },
                {
                  text: hasTranscription ? message.transcription : 'Clique para ouvir',
                  link: message.content,
                  style: 'audioLink',
                  ...(message.content && { decoration: 'underline' }),
                },
              ],
              width: '*',
            },
          ],
          columnGap: 5,
          margin: [0, 0, 0, 15],
        };
      }

      return result;
    };

    return [
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                stack: [
                  { ... (setupContent(message)) },
                  {
                    text: `${sender} - ${DateUtil.toFormart(message.createdAt, 'dd/MM/yyyy HH:mm:ss')}`,
                    style: 'timeStamp',
                    alignment: alignment,
                  },
                ],
                margin: [0, 0, 0, 0],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 10,
          paddingRight: () => 10,
          paddingTop: () => 6,
          paddingBottom: () => 6,
          fillColor: () => bubbleColor,
        },
        margin,
        alignment,
      },
    ]
  }

  private async downloadPdf(docDefinition: any, phoneNumber: string): Promise<void> {
    const fileName = `Trendmulti_Atendimento_${phoneNumber}.pdf`
    const pdfMake = await this.getPdfMake()
    pdfMake.createPdf(docDefinition).download(fileName)
  }

  private getPdfStyles(): Record<string, any> {
    return {
      header: {
        fontSize: 18,
        bold: true,
        margin: [0, 0, 0, 10],
        alignment: 'center',
      },
      subheader: {
        fontSize: 12,
        color: '#666',
        margin: [0, 0, 0, 5],
        alignment: 'center',
      },
      messageText: {
        fontSize: 11,
        color: '#000',
      },
      audioTitle: {
        fontSize: 11,
        bold: true,
        color: '#1f2937',
      },
      audioLink: {
        fontSize: 10,
        color: '#3b82f6',
      },
      timeStamp: {
        fontSize: 9,
        color: '#999',
      },
      footer: {
        fontSize: 10,
        color: '#666',
        margin: [0, 20, 0, 0],
      },
    }
  }

  async generatePDFAttendanceReport<T>(data: T[], filters?: any, tabulations?: boolean): Promise<void> {
    if (!data || data.length === 0) {
      console.warn('Nenhum dado disponível para exportação.');
      return;
    }

    const pdfMake = await this.getPdfMake();

    const filterContent: any[] = [];

    if (filters) {
      filterContent.push({ text: 'Filtros Utilizados', style: 'filterHeader' });

      if (filters.channel) filterContent.push({ text: `Canal: ${filters.channel}`, style: 'filterText' });
      if (filters.department) filterContent.push({ text: `Departamento: ${filters.department}`, style: 'filterText' });
      if (filters.user) filterContent.push({ text: `Atendente: ${filters.user}`, style: 'filterText' });
      if (filters.period) filterContent.push({ text: `Período: ${filters.period}`, style: 'filterText' });
      if (filters.status) filterContent.push({ text: `Status: ${filters.status}`, style: 'filterText' });

      filterContent.push('\n');
    }


    const tableBody = tabulations ? this.buildAttendanceTabulations(data) : this.buildAttendanceTable(data);
    const columnCount = tabulations ? this.reportAttendaceTabsFields.length : this.reportAttendaceFields.length;

    // Ajustar dinamicamente o número de colunas no tableBody
    tableBody.forEach(row => {
      if (row.length < columnCount) {
        while (row.length < columnCount) {
          row.push({ text: '-', style: 'tableCell' });
        }
      } else if (row.length > columnCount) {
        row.splice(columnCount); // Remove colunas extras
      }
    });

    const columnWidths = tabulations ? Array(columnCount).fill('*') : ['*', 55, '*', '*', '*', 60, '*', 35, 30, 65, 65];


    const docDefinition: any = {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins: [10, 10, 10, 10],
      content: [
        { text: 'Relatório de Atendimentos', style: 'header' },
        '\n',
        ...filterContent,
        {
          table: {
            headerRows: 1,
            widths: columnWidths,
            body: tableBody
          },
          layout: {
            fillColor: (rowIndex: number) =>
              rowIndex === 0 ? null : rowIndex % 2 === 0 ? '#f9fafb' : null,
            hLineColor: () => '#e5e7eb',
            vLineColor: () => '#e5e7eb',
            paddingLeft: () => 2,
            paddingRight: () => 2,
            paddingTop: () => 2,
            paddingBottom: () => 2,
          },
        },
      ],
      styles: {
        ...this.getPdfListStyles(),
        filterHeader: {
          fontSize: 11,
          bold: true,
          margin: [0, 0, 0, 2],
          color: '#333333',
        },
        filterText: {
          fontSize: 9,
          margin: [0, 0, 0, 1],
          color: '#333333',
        },
      },
    };

    const formattedDate = formatDate(new Date(), 'dd-MM-yyyy', 'pt-BR');
    const pdfDocGenerator = pdfMake.createPdf(docDefinition);

    pdfDocGenerator.getBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Trendmulti_Relatorio_Lista_Atendimento_${formattedDate}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  private buildAttendanceTable(data: AttendanceReportItem[]): any[] {

    const headers = this.reportAttendaceFields.map(field => ({
      text: field.header,
      style: 'tableHeader',
    }));

    const rows = data.map((item: AttendanceReportItem) => [
      { text: UtilHelper.substringBySize(item.contact?.name, 25), style: 'tableCell' },
      { text: UtilHelper.substringBySize(item.contact?.phone, 13), style: 'tableCell' },
      { text: UtilHelper.substringBySize(item.channel?.name, 30), style: 'tableCell' },
      { text: UtilHelper.substringBySize(item.department?.name, 30), style: 'tableCell' },
      { text: UtilHelper.substringBySize(item?.user?.name, 30), style: 'tableCell' },
      {
        text: String(item.status || '-'),
        style: 'tableCell',
        fillColor: this.getStatusColor(item.status),
        color: 'white',
        bold: true,
      },
      { text: UtilHelper.substringBySize(item.tags || '-', 40), style: 'tableCell' },
      {
        text: String(this.getFeedback(item.feedback)),
        style: 'tableCell',
        alignment: 'center',
      },
      { text: String(item.duration || '-'), style: 'tableCell' },
      { text: String(item.createdAt || '-'), style: 'tableCell' },
      { text: String(item.closedAt || '-'), style: 'tableCell' },
    ])

    return [headers, ...rows]
  }

  private buildAttendanceTabulations(data: AttendanceReportItem[]) {
    const headers = this.reportAttendaceTabsFields.map(field => ({
      text: field.header,
      style: 'tableHeader',
    }));

    const rows = data.map((item: AttendanceReportItem) => [
      { text: String(item.createdAt || '-'), style: 'tableCell' },
      { text: UtilHelper.substringBySize(item.contact?.name, 30), style: 'tableCell' },
      { text: UtilHelper.substringBySize(item.user?.name, 25), style: 'tableCell' },
      { text: UtilHelper.substringBySize(item.channel?.name, 20), style: 'tableCell' },
      { text: UtilHelper.substringBySize(item.department?.name || item.department?.description || '-', 25), style: 'tableCell' },
      { text: String(item.tabulations || '-'), style: 'tableCell' },
      { text: UtilHelper.substringBySize(item.observations || '-', 35), style: 'tableCell' },
      { text: String(item.duration || '-'), style: 'tableCell' },
    ]);
    return [headers, ...rows]
  }

  private getPdfListStyles(): Record<string, any> {
    return {
      header: {
        fontSize: 14,
        bold: true,
        margin: [0, 0, 0, 10],
        alignment: 'center',
        color: '#333333',
      },
      tableHeader: {
        bold: true,
        fontSize: 7,
        color: '#ffffff',
        fillColor: '#4B5563',
        alignment: 'center',
        margin: [0, 4, 0, 4],
      },
      tableCell: {
        fontSize: 6.5,
        color: '#111827',
        alignment: 'center',
        margin: [0, 4, 0, 4],
      },
    };
  }

  private getStatusColor(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'em andamento':
        return '#15803d'
      case 'fechado':
        return '#6b7280'
      case 'fora de horário':
        return '#dc2626'
      default:
        return '#9ca3af'
    }
  }

  private getFeedback(rating: number | string): string {
    const num = Number(rating)
    if (isNaN(num) || num <= 0) return '-'
    return `${num}/5`
  }
  async generatePDFReport<T>(data: T[], columns: ReportColumn[], filters?: Record<string, string>, title?: string, additionalData?: Record<string, any>): Promise<void> {

    const pdfMake = await this.getPdfMake();

    const tableHeaders = columns.map(col => ({ text: col.header, style: 'tableHeader' }));
    const tablesRows = data.map(item => {
      return columns.map(col => {
        const value = item[col.key];
        return { text: value ?? '-', style: 'tableCell', };
      });
    });

    const tableBody = [tableHeaders, ...tablesRows];

    const docDefinition: TDocumentDefinitions = {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins: [10, 10, 10, 10],
      content: [
        { text: title ?? 'Relatório', style: 'title', margin: [0, 0, 0, 10] },
        ...(filters ? this.buildPdfFilterSection(filters) : []),
        ...(additionalData ? this.buildPdfAdditionalDataSection(additionalData) : []),
        { text: ' ', margin: [0, 10] },
        {
          table: {
            headerRows: 1,
            widths: columns.map((c) => c.width),
            body: tableBody,
          }
        }
      ],
      styles: {
        ...this.getPdfListStyles(),
        filterHeader: {
          fontSize: 11,
          bold: true,
          margin: [0, 0, 0, 2],
          color: '#333333',
        },
        filterText: {
          fontSize: 9,
          margin: [0, 0, 0, 1],
          color: '#333333',
        },
        additionalDataHeader: {
          fontSize: 10,
          bold: true,
          margin: [0, 0, 0, 2],
          color: '#333333',
        },
        additionalDataText: {
          fontSize: 9,
          margin: [0, 0, 0, 1],
          color: '#333333',
        },
      },
    }

    const formattedDate = formatDate(new Date(), 'dd-MM-yyyy', 'pt-BR');
    const pdfDocGenerator = pdfMake.createPdf(docDefinition);
    pdfDocGenerator.getBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Trendmulti_Relatorio_Monitor_de_Vendas_${formattedDate}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  private buildPdfFilterSection(filters: Record<string, string>): Content[] {
    if (!filters || Object.keys(filters).length === 0) return [];

    const section: Content[] = [{ text: 'Filtros Utilizados', style: 'filterHeader', margin: [0, 0, 0, 5] }];

    for (const [label, value] of Object.entries(filters)) {
      section.push({ text: `${label}: ${value}`, style: 'filterText', margin: [0, 0, 0, 2] });
    }
    return section;
  }

  private buildPdfAdditionalDataSection(additionalData: Record<string, any>): Content[] {
    if (!additionalData || Object.keys(additionalData).length === 0) return [];

    const section: Content[] = [{ text: 'Dados Adicionais', style: 'additionalDataHeader', margin: [0, 5, 0, 5] }];

    const tables: Content[] = [];

    if (additionalData['kpis']) {
      const kpiTableBody = Object.entries(additionalData['kpis']).map(([label, value]) => [
        { text: label, style: 'tableCell' },
        { text: value, style: 'tableCell' },
      ]);
      const kpiHeaders = ['Indicador', 'Valor'].map(header => ({ text: header, style: 'tableHeader' }));
      tables.push({
        table: {
          widths: ['*', '*'],
          body: [kpiHeaders, ...kpiTableBody] as any,
        },
        layout: 'lightHorizontalLines',
      });
    }

    if (additionalData['salesBySource']?.length) {
      const salesBySourceTableBody = additionalData['salesBySource'].map(source => [
        { text: source.name, style: 'tableCell' },
        { text: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(source.value), style: 'tableCell' },
      ]);
      tables.push({
        table: {
          widths: ['*', '*'],
          body: [['Origem', 'Valor'].map(header => ({ text: header, style: 'tableHeader' })), ...salesBySourceTableBody],
        },
        layout: 'lightHorizontalLines',
      });
    }

    if (additionalData['salesByStatus']?.length) {
      const salesByStatusTableBody = additionalData['salesByStatus'].flatMap(status => [
        [{ text: `Data: ${status.name}`, colSpan: 2, style: 'tableCell' }],
        ...status.series.map(serie => [
          { text: serie.name, style: 'tableCell' },
          { text: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(serie.value), style: 'tableCell' },
        ]),
      ]);
      tables.push({
        table: {
          widths: ['*', '*'],
          body: [['Status', 'Valor'].map(header => ({ text: header, style: 'tableHeader' })), ...salesByStatusTableBody],
        },
        layout: 'lightHorizontalLines',
      });
    }

    if (tables.length > 0) {
      section.push({
        columns: tables.map(table => ({
          stack: [table],
          width: '33%',
          margin: [5, 0],
        })),
      });
    }

    return section;
  }

  private reportLeadSourceAttendanceFields: ReportColumn[] = [
    { header: 'Nome', key: 'name', width: 30 },
    { header: 'Telefone', key: 'phone', width: 20 },
    { header: 'Origem', key: 'leadSource', width: 25 },
    { header: 'Atendente', key: 'user', width: 25 },
    { header: 'Status', key: 'status', width: 20 },
    { header: 'Início', key: 'createdAt', width: 25 },
  ];

  private reportLeadSourceConversionFields: ReportColumn[] = [
    { header: 'Origem', key: 'leadSourceName', width: 30 },
    { header: 'Total', key: 'totalAttendances', width: 15 },
    { header: 'Fechados', key: 'wonAttendances', width: 15 },
    { header: 'Taxa de Conversão (%)', key: 'conversionRate', width: 22 },
  ];

  async generateExcelLeadSourceReport(data: any[], leadSources: LeadSourceItem[] = [], kpis?: LeadSourceKpisResult['kpis'], filters?: any,): Promise<ArrayBuffer> {
    const ExcelJS = await this.getExcelJS();
    const workbook = new ExcelJS.Workbook();

    const attendancesSheet = workbook.addWorksheet('Atendimentos');
    attendancesSheet.columns = this.reportLeadSourceAttendanceFields;
    const totalAttCols = this.reportLeadSourceAttendanceFields.length;
    const attEndColumn = this.getExcelColumnLetter(totalAttCols);

    let currentRow = 1;
    if (filters && Object.keys(filters).length > 0) {
      const filterTitleRow = attendancesSheet.insertRow(currentRow, []);
      filterTitleRow.getCell(1).value = 'Filtros Utilizados';
      filterTitleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
      filterTitleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4B5563' } };
      filterTitleRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
      attendancesSheet.mergeCells(`A${filterTitleRow.number}:${attEndColumn}${filterTitleRow.number}`);
      currentRow++;

      if (filters.period) {
        const row = attendancesSheet.insertRow(currentRow, []);
        row.getCell(1).value = `Período: ${filters.period}`;
        row.getCell(1).font = { bold: true, size: 12 };
        attendancesSheet.mergeCells(`A${row.number}:${attEndColumn}${row.number}`);
        currentRow++;
      }

      const blankRow = attendancesSheet.insertRow(currentRow, []);
      attendancesSheet.mergeCells(`A${blankRow.number}:${attEndColumn}${blankRow.number}`);
      currentRow++;
    }

    const headerRowIndex = currentRow;
    attendancesSheet.insertRow(headerRowIndex, this.reportLeadSourceAttendanceFields.map(c => c.header));
    attendancesSheet.getRow(headerRowIndex).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4B5563' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });

    data.forEach((item) => {
      attendancesSheet.addRow({
        name: item.contact?.name || '-',
        phone: item.contact?.phone || '-',
        leadSource: item.leadSource?.name || '-',
        user: item.user?.name || '-',
        status: item.status || '-',
        createdAt: item.createdAt || '-',
      });
    });

    attendancesSheet.eachRow((row, rowNumber) => {
      if (rowNumber > headerRowIndex) {
        row.eachCell((cell) => {
          cell.font = { size: 10, color: { argb: 'FF111827' } };
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });
        if (rowNumber % 2 === 0) {
          row.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
          });
        }
      }
    });

    const conversionSheet = workbook.addWorksheet('Conversões por Origem');
    conversionSheet.columns = this.reportLeadSourceConversionFields;
    const conversionHeaderRow = conversionSheet.getRow(1);
    conversionHeaderRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4B5563' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });

    leadSources.forEach((source) => {
      conversionSheet.addRow({
        leadSourceName: source.leadSourceName || '-',
        totalAttendances: source.totalAttendances ?? 0,
        wonAttendances: source.wonAttendances ?? 0,
        conversionRate: `${source.conversionRate ?? 0}%`,
      });
    });

    conversionSheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) {
        row.eachCell((cell) => {
          cell.font = { size: 10, color: { argb: 'FF111827' } };
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });
        if (rowNumber % 2 === 1) {
          row.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
          });
        }
      }
    });

    if (kpis) {
      const summaryData: ExcelReportSummaryData = {
        kpis: {
          'Total de Atendimentos': String(kpis.totalAttendances ?? 0),
          'Com Origem Identificada': `${kpis.withLeadSource?.count ?? 0} (${kpis.withLeadSource?.percent ?? 0}%)`,
          'Origem - Top Conversão': `${kpis.topConversionSource?.name ?? '-'} (${kpis.topConversionSource?.conversionRate ?? 0}%)`,
          'Origem - Maior Volume': `${kpis.topVolumeSource?.name ?? '-'} (${kpis.topVolumeSource?.totalAttendances ?? 0})`,
        },
      };
      this.createSummarySheet(workbook, summaryData);
    }

    return await workbook.xlsx.writeBuffer();
  }

  async generatePDFLeadSourceReport(
    data: any[],
    leadSources: LeadSourceItem[] = [],
    kpis?: LeadSourceKpisResult['kpis'],
    filters?: any,
  ): Promise<void> {
    const pdfMake = await this.getPdfMake();

    const filterContent: any[] = [];
    if (filters) {
      filterContent.push({ text: 'Filtros Utilizados', style: 'filterHeader' });
      if (filters.period) filterContent.push({ text: `Período: ${filters.period}`, style: 'filterText' });
      filterContent.push('\n');
    }

    const kpiContent: any[] = [];
    if (kpis) {
      kpiContent.push({ text: 'Indicadores', style: 'sectionHeader' });
      kpiContent.push({
        table: {
          headerRows: 1,
          widths: ['*', '*', '*', '*'],
          body: [
            [
              { text: 'Total de Atendimentos', style: 'tableHeader' },
              { text: 'Com Origem Identificada', style: 'tableHeader' },
              { text: 'Origem - Top Conversão', style: 'tableHeader' },
              { text: 'Origem - Maior Volume', style: 'tableHeader' },
            ],
            [
              { text: String(kpis.totalAttendances ?? 0), style: 'tableCell' },
              { text: `${kpis.withLeadSource?.count ?? 0} (${kpis.withLeadSource?.percent ?? 0}%)`, style: 'tableCell' },
              { text: `${kpis.topConversionSource?.name ?? '-'} (${kpis.topConversionSource?.conversionRate ?? 0}%)`, style: 'tableCell' },
              { text: `${kpis.topVolumeSource?.name ?? '-'} (${kpis.topVolumeSource?.totalAttendances ?? 0})`, style: 'tableCell' },
            ],
          ],
        },
        layout: {
          fillColor: (rowIndex: number) => (rowIndex === 0 ? null : rowIndex % 2 === 0 ? '#f9fafb' : null),
          hLineColor: () => '#e5e7eb',
          vLineColor: () => '#e5e7eb',
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 4,
          paddingBottom: () => 4,
        },
      });
      kpiContent.push('\n');
    }

    const conversionContent: any[] = [];
    if (leadSources?.length) {
      const conversionHeaders = this.reportLeadSourceConversionFields.map(field => ({
        text: field.header,
        style: 'tableHeader',
      }));
      const conversionRows = leadSources.map((source) => [
        { text: UtilHelper.substringBySize(source.leadSourceName || '-', 30), style: 'tableCell' },
        { text: String(source.totalAttendances ?? 0), style: 'tableCell' },
        { text: String(source.wonAttendances ?? 0), style: 'tableCell' },
        { text: `${source.conversionRate ?? 0}%`, style: 'tableCell' },
      ]);
      conversionContent.push({ text: 'Conversões por Origem', style: 'sectionHeader' });
      conversionContent.push({
        table: {
          headerRows: 1,
          widths: ['*', '*', '*', '*'],
          body: [conversionHeaders, ...conversionRows],
        },
        layout: {
          fillColor: (rowIndex: number) => (rowIndex === 0 ? null : rowIndex % 2 === 0 ? '#f9fafb' : null),
          hLineColor: () => '#e5e7eb',
          vLineColor: () => '#e5e7eb',
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 4,
          paddingBottom: () => 4,
        },
      });
      conversionContent.push('\n');
    }

    const attendanceHeaders = this.reportLeadSourceAttendanceFields.map(field => ({
      text: field.header,
      style: 'tableHeader',
    }));
    const attendanceRows = (data || []).map((item: any) => [
      { text: UtilHelper.substringBySize(item.contact?.name || '-', 25), style: 'tableCell' },
      { text: UtilHelper.substringBySize(item.contact?.phone || '-', 15), style: 'tableCell' },
      { text: UtilHelper.substringBySize(item.leadSource?.name || '-', 25), style: 'tableCell' },
      { text: UtilHelper.substringBySize(item.user?.name || '-', 25), style: 'tableCell' },
      {
        text: String(item.status || '-'),
        style: 'tableCell',
        fillColor: this.getStatusColor(item.status),
        color: 'white',
        bold: true,
      },
      { text: String(item.createdAt || '-'), style: 'tableCell' },
    ]);

    const docDefinition: any = {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins: [10, 10, 10, 10],
      content: [
        { text: 'Relatório de Origem do Lead', style: 'header' },
        '\n',
        ...filterContent,
        ...kpiContent,
        ...conversionContent,
        { text: 'Lista de Atendimentos', style: 'sectionHeader' },
        {
          table: {
            headerRows: 1,
            widths: ['*', '*', '*', '*', 60, '*'],
            body: [attendanceHeaders, ...attendanceRows],
          },
          layout: {
            fillColor: (rowIndex: number) => (rowIndex === 0 ? null : rowIndex % 2 === 0 ? '#f9fafb' : null),
            hLineColor: () => '#e5e7eb',
            vLineColor: () => '#e5e7eb',
            paddingLeft: () => 2,
            paddingRight: () => 2,
            paddingTop: () => 2,
            paddingBottom: () => 2,
          },
        },
      ],
      styles: {
        ...this.getPdfListStyles(),
        filterHeader: {
          fontSize: 11,
          bold: true,
          margin: [0, 0, 0, 2],
          color: '#333333',
        },
        filterText: {
          fontSize: 9,
          margin: [0, 0, 0, 1],
          color: '#333333',
        },
        sectionHeader: {
          fontSize: 12,
          bold: true,
          margin: [0, 6, 0, 6],
          color: '#111827',
        },
      },
    };

    const formattedDate = formatDate(new Date(), 'dd-MM-yyyy', 'pt-BR');
    const pdfDocGenerator = pdfMake.createPdf(docDefinition);
    pdfDocGenerator.getBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Trendmulti_Relatorio_Origem_Lead_${formattedDate}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }
}
